<script lang="ts">
    import * as Sentry from '@sentry/browser';
    import { createEventDispatcher } from 'svelte';

    import { trans } from '$assets/translator.js';
    import DownloadIcon from '$backoffice/icons/Download.svelte';
    import { uploadImageToMedia } from '$backoffice/views/MediaManager/utils';
    import cn from '$common/utils/cn';
    import Button from '$saga/components/Button.svelte';

    import FocalPointEditor from './FocalPointEditor.svelte';
    import ImageCropEditor from './ImageCropEditor.svelte';

    export let apiEndpoint: string;
    export let mediaBaseUrl: string;
    export let cropProxyUrl: string;

    let isHovering: boolean = false;
    let isUploading: boolean = false;
    let uploadError: string | null = null;

    const dispatch = createEventDispatcher<{
        uploading: { tempId: string; file: File };
        uploaded: {
            tempId: string;
            path: string;
            width: number;
            height: number;
            focalPoint?: { x: number; y: number };
        };
        error: { tempId: string; error: string };
    }>();

    let fileInput: HTMLInputElement;

    // Crop queue: files waiting to be cropped one at a time
    let cropQueue: File[] = [];
    let currentCropFile: File | null = null;
    let croppedFile: File | null = null;
    let croppedBlobUrl: string = '';

    function advanceCropQueue() {
        if (cropQueue.length > 0) {
            currentCropFile = cropQueue[0];
            cropQueue = cropQueue.slice(1);
        } else {
            currentCropFile = null;
        }
    }

    function handleCropConfirmed(event: CustomEvent<{ file: File }>) {
        croppedFile = event.detail.file;
        croppedBlobUrl = URL.createObjectURL(croppedFile);
    }

    function handleCropCancelled() {
        cropQueue = [];
        currentCropFile = null;
    }

    function handleFocalPointConfirmed(
        event: CustomEvent<{ focalPoint: { x: number; y: number } }>,
    ) {
        const file = croppedFile!;
        URL.revokeObjectURL(croppedBlobUrl);
        croppedBlobUrl = '';
        croppedFile = null;
        uploadSingleFile(file, event.detail.focalPoint);
        advanceCropQueue();
    }

    function handleFocalPointCancelled() {
        URL.revokeObjectURL(croppedBlobUrl);
        croppedBlobUrl = '';
        croppedFile = null;
        // currentCropFile still set → ImageCropper re-appears
    }

    const triggerFileInput = () => {
        fileInput?.click();
    };

    const handleFileSelect = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (target.files) {
            await uploadFiles(Array.from(target.files));
        }
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleDrop = async (event: DragEvent) => {
        event.preventDefault();
        isHovering = false;
        if (event.dataTransfer?.files) {
            await uploadFiles(Array.from(event.dataTransfer.files));
        }
    };

    const handleDragOver = (event: DragEvent) => {
        event.preventDefault();
        isHovering = true;
    };

    const handleDragLeave = () => {
        isHovering = false;
    };

    const uploadSingleFile = async (
        file: File,
        focalPoint?: { x: number; y: number },
    ) => {
        isUploading = true;
        uploadError = null;

        const tempId = Date.now() + '-' + file.name;
        dispatch('uploading', { tempId, file });

        try {
            const uploadedImage = await uploadImageToMedia(
                apiEndpoint,
                mediaBaseUrl,
                file,
            );

            dispatch('uploaded', {
                tempId,
                path: uploadedImage.url,
                width: uploadedImage.width,
                height: uploadedImage.height,
                focalPoint,
            });
        } catch (error: unknown) {
            console.error(`Upload error for ${file.name}:`, error);
            Sentry.captureException(error);
            const message =
                error instanceof Error
                    ? error.message
                    : `An unknown error occurred while uploading ${file.name}.`;
            dispatch('error', { tempId, error: message });
            if (!uploadError)
                uploadError =
                    'Un ou plusieurs fichiers ont échoué. Votre photo est probablement trop lourde, veuillez la compresser';
        } finally {
            if (cropQueue.length === 0 && !currentCropFile) {
                isUploading = false;
            }
        }
    };

    const uploadFiles = async (files: File[]) => {
        if (files.length === 0) return;
        uploadError = null;
        cropQueue = files.slice(1);
        currentCropFile = files[0];
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerFileInput();
        }
    };
</script>

{#if croppedFile && croppedBlobUrl}
    <FocalPointEditor
        src={croppedBlobUrl}
        initialFocalPoint={{ x: 50, y: 50 }}
        on:confirm={handleFocalPointConfirmed}
        on:cancel={handleFocalPointCancelled}
    />
{:else if currentCropFile}
    <!-- used to mount and remount modal with the next image imported -->
    {#key currentCropFile}
        <ImageCropEditor
            file={currentCropFile}
            {cropProxyUrl}
            on:cropped={handleCropConfirmed}
            on:cancel={handleCropCancelled}
        />
    {/key}
{/if}

<div
    class={cn(
        'border-2 border-dashed p-m text-center transition-colors',
        'border-border-quiet',
        isHovering && !isUploading && 'border-content-quiet bg-container-quiet',
        isUploading
            ? 'bg-container-lowest cursor-not-allowed opacity-70'
            : 'cursor-pointer',
    )}
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    on:drop={isUploading ? undefined : handleDrop}
    on:click={isUploading ? undefined : triggerFileInput}
    role="button"
    tabindex={isUploading ? -1 : 0}
    on:keydown={isUploading ? undefined : handleKeyDown}
>
    <input
        type="file"
        bind:this={fileInput}
        on:change={handleFileSelect}
        accept="image/*"
        multiple
        class="hidden"
        disabled={isUploading}
    />

    {#if isUploading}
        <p class="mt-xs text-sm text-content-quiet">
            {trans('form.multi_image_upload.sending')}
        </p>
    {:else}
        <div class="text-sm">{trans('form.multi_image_upload.drop_image')}</div>
        <div class="my-2xs text-sm">ou</div>
        <Button
            type="button"
            intent="tertiary"
            size="small"
            disabled={isUploading}
            tabindex={-1}
            startIcon={DownloadIcon}
        >
            {trans('form.multi_image_upload.add')}
        </Button>
        <div class="mt-xs text-sm">
            {trans('form.multi_image_upload.size_limit')}
        </div>
    {/if}

    {#if uploadError}
        <p class="mt-xs text-sm text-status-negative">
            {trans('form.multi_image_upload.error')}
            {uploadError}
        </p>
    {/if}
</div>
